import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Visibility as InfoIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  OpenInNew as OpenInNewIcon,
  VisibilityOutlined as ViewIcon,
  Download as DownloadIcon,
  CloudUpload as UploadIcon,
} from "@mui/icons-material";
import { ndaService, buildFileUrl } from "../../services/api";

const getApprovalStatusChip = (status) => {
  const statusMap = {
    pending_approval: { label: "Pendiente", color: "warning" },
    approved: { label: "Aprobado", color: "success" },
    rejected: { label: "Rechazado", color: "error" },
  };
  const config = statusMap[status] || { label: status, color: "default" };
  return <Chip label={config.label} color={config.color} size="small" />;
};

const getSignatureStatusChip = (status) => {
  const statusMap = {
    pending: { label: "Pendiente", color: "default" },
    pending_upload: { label: "Pendiente de carga", color: "info" },
    pending_legal: { label: "Pendiente aprobacion", color: "warning" },
    signed: { label: "Firmado", color: "success" },
    rejected: { label: "Rechazado", color: "error" },
    revoked: { label: "Revocado", color: "default" },
  };
  const config = statusMap[status] || { label: status, color: "default" };
  return <Chip label={config.label} color={config.color} size="small" />;
};

const LegalNDAManagement = () => {
  const [ndas, setNdas] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [mainTabValue, setMainTabValue] = useState(0);
  const [ndaTabValue, setNdaTabValue] = useState(0);
  const [sigTabValue, setSigTabValue] = useState(0);

  // Dialog states
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [selectedNda, setSelectedNda] = useState(null);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectComments, setRejectComments] = useState("");
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectingType, setRejectingType] = useState(null); // 'nda' or 'signature'
  const [approvingNdaId, setApprovingNdaId] = useState(null);
  const [approvingSigId, setApprovingSigId] = useState(null);

  // Legal file upload states
  const [legalUploadFile, setLegalUploadFile] = useState({});
  const [uploadingLegalFile, setUploadingLegalFile] = useState(null);
  const [downloadingUserFile, setDownloadingUserFile] = useState(null);

  const ndaTabFilters = [null, "pending_approval", "approved", "rejected"];
  const sigTabFilters = [null, "pending_legal", "signed", "rejected"];

  const loadNdas = async () => {
    try {
      setLoading(true);
      const params = {};
      const filter = ndaTabFilters[ndaTabValue];
      if (filter) {
        params.approvalStatus = filter;
      }
      const response = await ndaService.getAllForLegal(params);
      setNdas(response.data.ndas || []);
    } catch (err) {
      setError("Error al cargar NDAs");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSignatures = async () => {
    try {
      setLoading(true);
      const params = {};
      const filter = sigTabFilters[sigTabValue];
      if (filter) {
        params.status = filter;
      }
      const response = await ndaService.getPendingSignatures(params);
      setSignatures(response.data.signatures || []);
    } catch (err) {
      setError("Error al cargar solicitudes de acceso");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mainTabValue === 0) {
      loadNdas();
    } else {
      loadSignatures();
    }
  }, [mainTabValue, ndaTabValue, sigTabValue]);

  // NDA template approval
  const handleApproveNda = async (ndaId) => {
    try {
      setApprovingNdaId(ndaId);
      setError("");
      await ndaService.approve(ndaId, "Aprobado por el estudio juridico");
      setSuccess("NDA aprobado exitosamente");
      await loadNdas();
    } catch (err) {
      setError(err.response?.data?.error || "Error al aprobar NDA");
    } finally {
      setApprovingNdaId(null);
    }
  };

  // Signature approval (access request)
  const handleApproveSignature = async (sigId) => {
    try {
      setApprovingSigId(sigId);
      setError("");
      await ndaService.approveSignature(sigId, "Aprobado por el estudio juridico");
      setSuccess("Solicitud de acceso aprobada. El usuario ya tiene acceso al proyecto.");
      await loadSignatures();
    } catch (err) {
      setError(err.response?.data?.error || "Error al aprobar solicitud");
    } finally {
      setApprovingSigId(null);
    }
  };

  const handleOpenReject = (id, type) => {
    setRejectingId(id);
    setRejectingType(type);
    setRejectComments("");
    setOpenRejectDialog(true);
  };

  const handleReject = async () => {
    if (!rejectComments.trim()) {
      setError("Debe proporcionar un motivo para rechazar");
      return;
    }

    try {
      setError("");
      if (rejectingType === "nda") {
        await ndaService.reject(rejectingId, rejectComments);
        setSuccess("NDA rechazado");
        await loadNdas();
      } else {
        await ndaService.rejectSignature(rejectingId, rejectComments);
        setSuccess("Solicitud de acceso rechazada");
        await loadSignatures();
      }
      setOpenRejectDialog(false);
      setRejectingId(null);
      setRejectingType(null);
      setRejectComments("");
    } catch (err) {
      setError(err.response?.data?.error || "Error al rechazar");
    }
  };

  const handleShowInfo = (nda) => {
    setSelectedNda(nda);
    setOpenInfoDialog(true);
  };

  // Descargar NDA subido por el usuario
  const handleDownloadUserUpload = async (sigId) => {
    try {
      setDownloadingUserFile(sigId);
      setError("");
      const response = await ndaService.downloadUserUpload(sigId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = response.headers["content-disposition"];
      const fileName = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
        : "nda-usuario.pdf";
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || "Error al descargar NDA del usuario");
    } finally {
      setDownloadingUserFile(null);
    }
  };

  // Descargar NDA firmado por legal
  const handleDownloadLegalUpload = async (sigId) => {
    try {
      setDownloadingUserFile(sigId);
      setError("");
      const response = await ndaService.downloadLegalUpload(sigId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = response.headers["content-disposition"];
      const fileName = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
        : "nda-legal.pdf";
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || "Error al descargar NDA firmado por legal");
    } finally {
      setDownloadingUserFile(null);
    }
  };

  // Subir NDA firmado por legal
  const handleUploadLegalSigned = async (sigId) => {
    try {
      const file = legalUploadFile[sigId];
      if (!file) {
        setError("Seleccione un archivo para subir");
        return;
      }
      setUploadingLegalFile(sigId);
      setError("");
      const formData = new FormData();
      formData.append("file", file);
      await ndaService.uploadLegalSigned(sigId, formData);
      setSuccess("NDA firmado subido exitosamente. Ahora puede aprobar la solicitud.");
      setLegalUploadFile((prev) => {
        const updated = { ...prev };
        delete updated[sigId];
        return updated;
      });
      await loadSignatures();
    } catch (err) {
      setError(err.response?.data?.error || "Error al subir NDA firmado");
    } finally {
      setUploadingLegalFile(null);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" component="h1">
            Panel de Estudio Juridico
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Gestione NDAs y solicitudes de acceso a proyectos
          </Typography>
        </Paper>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Main Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs
            value={mainTabValue}
            onChange={(e, newVal) => setMainTabValue(newVal)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Aprobacion de NDAs" />
            <Tab label="Solicitudes de Acceso" />
          </Tabs>
        </Paper>

        {/* NDA Templates Tab */}
        {mainTabValue === 0 && (
          <>
            {/* Sub-tabs for NDA filtering */}
            <Paper sx={{ mb: 2 }}>
              <Tabs
                value={ndaTabValue}
                onChange={(e, newVal) => setNdaTabValue(newVal)}
                indicatorColor="secondary"
                textColor="secondary"
                variant="scrollable"
              >
                <Tab label="Todos" />
                <Tab label="Pendientes" />
                <Tab label="Aprobados" />
                <Tab label="Rechazados" />
              </Tabs>
            </Paper>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Titulo</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Creador</TableCell>
                    <TableCell>Proyecto</TableCell>
                    <TableCell align="center">Estado Legal</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ndas.map((nda) => (
                    <TableRow key={nda.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{nda.title}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {nda.NDAType?.title || "Sin tipo"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {nda.User?.name || "Desconocido"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {nda.User?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {nda.ResearchProject?.title || "Sin proyecto"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {getApprovalStatusChip(nda.approvalStatus)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleShowInfo(nda)}
                          title="Ver detalle"
                          sx={{ color: "#2196f3" }}
                        >
                          <InfoIcon />
                        </IconButton>
                        {nda.approvalStatus === "pending_approval" && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleApproveNda(nda.id)}
                              title="Aprobar NDA"
                              sx={{ color: "#4caf50" }}
                              disabled={approvingNdaId === nda.id}
                            >
                              {approvingNdaId === nda.id ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <ApproveIcon />
                              )}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenReject(nda.id, "nda")}
                              title="Rechazar NDA"
                              color="error"
                            >
                              <RejectIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {ndas.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No hay NDAs en esta categoria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Access Requests (Signatures) Tab */}
        {mainTabValue === 1 && (
          <>
            {/* Sub-tabs for signature filtering */}
            <Paper sx={{ mb: 2 }}>
              <Tabs
                value={sigTabValue}
                onChange={(e, newVal) => setSigTabValue(newVal)}
                indicatorColor="secondary"
                textColor="secondary"
                variant="scrollable"
              >
                <Tab label="Todas" />
                <Tab label="Pendientes" />
                <Tab label="Aprobadas" />
                <Tab label="Rechazadas" />
              </Tabs>
            </Paper>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Solicitante</TableCell>
                    <TableCell>Proyecto</TableCell>
                    <TableCell>NDA</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {signatures.map((sig) => (
                    <TableRow key={sig.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {sig.User?.name || "Desconocido"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {sig.User?.email}
                        </Typography>
                        {sig.User?.role && (
                          <Typography variant="caption" display="block" color="textSecondary">
                            Rol: {sig.User.role}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {sig.ResearchProject?.title || "Sin proyecto"}
                        </Typography>
                        {sig.ResearchProject?.User && (
                          <Typography variant="caption" color="textSecondary">
                            Creador: {sig.ResearchProject.User.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {sig.NDA?.title || "Sin NDA"}
                        </Typography>
                        {sig.NDA?.NDAType && (
                          <Typography variant="caption" color="textSecondary">
                            Tipo: {sig.NDA.NDAType.title}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {getSignatureStatusChip(sig.status)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(sig.createdAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {sig.status === "pending_legal" && (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start" }}>
                            {/* Blockchain hash */}
                            {sig.blockchainTxHash && (
                              <Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all", maxWidth: 200 }}>
                                Tx: {sig.blockchainTxHash.substring(0, 16)}...
                              </Typography>
                            )}

                            {/* Descargar NDA del usuario */}
                            {sig.userFilePath && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadUserUpload(sig.id)}
                                disabled={downloadingUserFile === sig.id}
                              >
                                {downloadingUserFile === sig.id ? "Descargando..." : "Descargar NDA usuario"}
                              </Button>
                            )}

                            {/* Subir NDA firmado por legal */}
                            {!sig.legalFilePath ? (
                              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                                <input
                                  accept=".pdf"
                                  style={{ display: "none" }}
                                  id={`legal-upload-${sig.id}`}
                                  type="file"
                                  onChange={(e) =>
                                    setLegalUploadFile((prev) => ({
                                      ...prev,
                                      [sig.id]: e.target.files[0],
                                    }))
                                  }
                                />
                                <label htmlFor={`legal-upload-${sig.id}`}>
                                  <Button variant="outlined" component="span" size="small" startIcon={<UploadIcon />}>
                                    {legalUploadFile[sig.id] ? legalUploadFile[sig.id].name : "Seleccionar NDA firmado"}
                                  </Button>
                                </label>
                                {legalUploadFile[sig.id] && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => handleUploadLegalSigned(sig.id)}
                                    disabled={uploadingLegalFile === sig.id}
                                  >
                                    {uploadingLegalFile === sig.id ? "Subiendo..." : "Subir"}
                                  </Button>
                                )}
                              </Box>
                            ) : (
                              <Chip label="NDA legal subido" color="info" size="small" variant="outlined" />
                            )}

                            {/* Aprobar / Rechazar */}
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleApproveSignature(sig.id)}
                                title={sig.legalFilePath ? "Aprobar acceso" : "Debe subir NDA firmado primero"}
                                sx={{ color: sig.legalFilePath ? "#4caf50" : "#bdbdbd" }}
                                disabled={approvingSigId === sig.id || !sig.legalFilePath}
                              >
                                {approvingSigId === sig.id ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <ApproveIcon />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenReject(sig.id, "signature")}
                                title="Rechazar acceso"
                                color="error"
                              >
                                <RejectIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        )}
                        {sig.status === "pending_upload" && (
                          <Typography variant="caption" color="textSecondary">
                            El usuario aun no ha subido el NDA
                          </Typography>
                        )}
                        {sig.status === "signed" && (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start" }}>
                            {sig.blockchainTxHash && (
                              <Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all", maxWidth: 200 }}>
                                Tx: {sig.blockchainTxHash.substring(0, 16)}...
                              </Typography>
                            )}
                            {sig.userFilePath && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadUserUpload(sig.id)}
                                disabled={downloadingUserFile === sig.id}
                              >
                                {downloadingUserFile === sig.id ? "Descargando..." : "Descargar NDA usuario"}
                              </Button>
                            )}
                            {sig.legalFilePath && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadLegalUpload(sig.id)}
                              >
                                Descargar NDA legal
                              </Button>
                            )}
                          </Box>
                        )}
                        {sig.ApprovedByLegal && (
                          <Typography variant="caption" display="block" color="textSecondary">
                            Por: {sig.ApprovedByLegal.name}
                          </Typography>
                        )}
                        {sig.legalComments && (
                          <Typography variant="caption" display="block" color="textSecondary" sx={{ fontStyle: "italic" }}>
                            {sig.legalComments}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {signatures.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No hay solicitudes en esta categoria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Info Dialog */}
        <Dialog
          open={openInfoDialog}
          onClose={() => setOpenInfoDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Detalle del NDA</DialogTitle>
          <DialogContent>
            {selectedNda && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Titulo:
                  </Typography>
                  <Typography variant="h6">{selectedNda.title}</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {selectedNda.NDAType && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Tipo de NDA:
                    </Typography>
                    <Typography variant="body2">
                      {selectedNda.NDAType.title}
                    </Typography>
                  </Box>
                )}

                {selectedNda.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Descripcion:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "pre-wrap" }}
                    >
                      {selectedNda.description}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Creado por:
                  </Typography>
                  <Typography variant="body2">
                    {selectedNda.User?.name} ({selectedNda.User?.email})
                  </Typography>
                </Box>

                {selectedNda.ResearchProject && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Proyecto:
                    </Typography>
                    <Typography variant="body2">
                      {selectedNda.ResearchProject.title}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Estado de Aprobacion Legal:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {getApprovalStatusChip(selectedNda.approvalStatus)}
                  </Box>
                </Box>

                {selectedNda.ApprovedByLegal && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Revisado por:
                    </Typography>
                    <Typography variant="body2">
                      {selectedNda.ApprovedByLegal.name} (
                      {selectedNda.ApprovedByLegal.email})
                    </Typography>
                  </Box>
                )}

                {selectedNda.approvalComments && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Comentarios:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        backgroundColor: "rgba(0, 0, 0, 0.05)",
                        padding: 1,
                        borderRadius: 1,
                      }}
                    >
                      {selectedNda.approvalComments}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {selectedNda.filePath && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Archivo:
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<ViewIcon />}
                        href={buildFileUrl(selectedNda.filePath, "ndas")}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver Archivo
                      </Button>
                    </Box>
                  </Box>
                )}

                {selectedNda.blockchainTxHash && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Blockchain:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        wordBreak: "break-all",
                        backgroundColor: "rgba(0, 0, 0, 0.05)",
                        padding: 1,
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      {selectedNda.blockchainTxHash}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      href={`https://polygonscan.com/tx/${selectedNda.blockchainTxHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver en PolygonScan
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {selectedNda?.approvalStatus === "pending_approval" && (
              <>
                <Button
                  onClick={() => {
                    handleApproveNda(selectedNda.id);
                    setOpenInfoDialog(false);
                  }}
                  color="success"
                  variant="contained"
                >
                  Aprobar
                </Button>
                <Button
                  onClick={() => {
                    setOpenInfoDialog(false);
                    handleOpenReject(selectedNda.id, "nda");
                  }}
                  color="error"
                  variant="contained"
                >
                  Rechazar
                </Button>
              </>
            )}
            <Button onClick={() => setOpenInfoDialog(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog
          open={openRejectDialog}
          onClose={() => setOpenRejectDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {rejectingType === "nda" ? "Rechazar NDA" : "Rechazar solicitud de acceso"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
              Indique el motivo del rechazo. Este comentario sera visible para el
              solicitante.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Motivo del rechazo"
              value={rejectComments}
              onChange={(e) => setRejectComments(e.target.value)}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRejectDialog(false)}>Cancelar</Button>
            <Button
              onClick={handleReject}
              color="error"
              variant="contained"
              disabled={!rejectComments.trim()}
            >
              Rechazar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default LegalNDAManagement;
